// Composer Studio — 노드 트리 deep clone + 서브트리 전체 id 재발급(복제/붙여넣기 공용).
//
// EditorPage에 로컬로 있던 cloneNodeWithNewIds를 테스트 가능하게 추출한 모듈. 동작은 그대로 유지하되,
// 복사/붙여넣기(P2)에서 여러 노드를 하나의 공유 existing 집합으로 순차 클론할 수 있도록 배치 변형
// cloneNodesWithNewIds를 추가한다. 순수 함수(structuredClone 기반)라 원본 노드는 불변이다.

import type { ComposerNode } from '@safetyhub/shared';

// 노드(및 모든 자식)를 deep clone하면서 트리 전체에 새 id를 부여한다(복제 시 id 충돌 회피).
// existing에는 기존 트리 + 새로 부여한 id가 누적되어, 같은 트리 내 형제 복제도 안전.
// prefix는 special 노드면 special 종류, 아니면 kind를 사용(handleAddNode와 동일 네이밍).
// idMap(옵셔널)을 주면 old id → new id 매핑을 채운다(결재란 배치 nodeId remap 등 참조 재매핑용).
export function cloneNodeWithNewIds(
  node: ComposerNode,
  existing: Set<string>,
  counter: { current: number },
  idMap?: Map<string, string>,
): ComposerNode {
  const clone = structuredClone(node);
  const reassign = (n: ComposerNode) => {
    const prefix = (n as { special?: string }).special ?? n.kind;
    const oldId = n.id;
    let newId: string;
    do {
      newId = `${prefix}-${++counter.current}`;
    } while (existing.has(newId));
    existing.add(newId);
    n.id = newId;
    idMap?.set(oldId, newId);
    if (n.kind === 'group' || n.kind === 'stack') n.children.forEach(reassign);
    else if (n.kind === 'table') n.cells.forEach((c) => c.content?.forEach(reassign));
    else if (n.kind === 'repeat') reassign(n.template);
    else if (n.kind === 'branch') {
      // 슬롯 id는 새 branch id 접두로 재생성(복제 유일성) + 슬롯 자식도 재귀 재할당.
      n.branches.forEach((slot, i) => {
        slot.id = `${n.id}-slot-${i}`;
        slot.children.forEach(reassign);
      });
    }
  };
  reassign(clone);
  return clone;
}

/**
 * 여러 노드를 **하나의 공유 existing 집합**으로 순차 클론한다(붙여넣기 배치 복제).
 *
 * 각 노드는 cloneNodeWithNewIds로 서브트리 전 id를 재발급하며, 같은 existing/counter를 넘겨
 * 노드 간 id 충돌이 없도록 한다(앞 노드가 부여한 id가 뒤 노드 클론에서 회피된다). 원본 배열·노드는
 * 불변(structuredClone). 삽입 순서를 보존한 클론 배열을 반환한다.
 * idMap(옵셔널)을 주면 전 노드의 old id → new id 매핑이 누적된다(참조 재매핑용).
 */
export function cloneNodesWithNewIds(
  nodes: ComposerNode[],
  existing: Set<string>,
  counter: { current: number },
  idMap?: Map<string, string>,
): ComposerNode[] {
  return nodes.map((n) => cloneNodeWithNewIds(n, existing, counter, idMap));
}
